<template>
  <div class="erp-sync-log-panel card-block">
    <div class="log-toolbar">
      <button class="btn btn-sm btn-outline-primary" :disabled="loading" @click="reload">
        {{ $t("tools.erp_integration.sync_log_refresh") }}
      </button>
      <span class="log-hint text-muted" v-if="!remoteAvailable">
        {{ $t("tools.erp_integration.sync_log_local_only") }}
      </span>
      <button
        type="button"
        class="btn btn-sm btn-link log-diag-toggle"
        v-if="localCount"
        @click="clearLocal"
      >
        {{ $t("tools.erp_integration.sync_log_clear_local", { count: localCount }) }}
      </button>
      <button
        type="button"
        class="btn btn-sm btn-link log-diag-toggle"
        @click="showDiagnostics = !showDiagnostics"
      >
        {{ $t("tools.erp_integration.sync_log_diagnostics") }}
      </button>
    </div>

    <div class="log-storage-warning" v-if="storageProblem">
      <strong>{{ $t("tools.erp_integration.sync_log_storage_missing") }}</strong>
      <span>{{ storageProblem }}</span>
    </div>

    <div class="log-diagnostics" v-if="showDiagnostics">
      <div class="log-detail__label">
        {{ $t("tools.erp_integration.sync_log_diagnostics_hint") }}
      </div>
      <pre class="log-detail__trace">{{ diagnosticsText }}</pre>
    </div>

    <DxDataGrid
      class="smagria-grid erp-sync-log-grid"
      :data-source="logs"
      key-expr="id"
      :show-borders="true"
      @option-changed="onOptionChanged"
      :show-column-lines="true"
      :show-row-lines="false"
      :row-alternation-enabled="true"
      :column-auto-width="false"
      :column-min-width="90"
      :no-data-text="$t('tools.erp_integration.sync_log_empty')"
      style="min-height: 320px"
    >
      <DxPaging :page-size="50" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="[20, 50, 100, 200]"
        display-mode="full"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxHeaderFilter :visible="true" />
      <DxFilterRow :visible="true" />
      <DxMasterDetail :enabled="true" template="logDetail" />

      <DxColumn
        data-field="created_at"
        data-type="datetime"
        :caption="$t('tools.erp_integration.sync_log_date')"
        format="yyyy.MM.dd HH:mm:ss"
        sort-order="desc"
        :width="160"
      />
      <DxColumn
        data-field="entity_label"
        :caption="$t('tools.erp_integration.sync_log_entity')"
        :width="180"
      />
      <DxColumn
        data-field="source_label"
        :caption="$t('tools.erp_integration.sync_log_source')"
        :width="110"
      />
      <DxColumn
        data-field="status"
        :caption="$t('tools.erp_integration.sync_log_status')"
        :width="110"
        cell-template="statusCell"
      />
      <DxColumn
        data-field="fetched_count"
        data-type="number"
        :caption="$t('tools.erp_integration.sync_log_fetched')"
        :width="100"
      />
      <DxColumn
        data-field="inserted_count"
        data-type="number"
        :caption="$t('tools.erp_integration.sync_log_inserted')"
        :width="100"
      />
      <DxColumn
        data-field="updated_count"
        data-type="number"
        :caption="$t('tools.erp_integration.sync_log_updated')"
        :width="100"
      />
      <DxColumn
        data-field="untouched_count"
        data-type="number"
        :caption="$t('tools.erp_integration.sync_log_untouched')"
        :width="110"
      />
      <DxColumn
        data-field="changed_count"
        data-type="number"
        :caption="$t('tools.erp_integration.sync_log_difference')"
        :width="110"
      />
      <DxColumn
        data-field="duration_ms"
        data-type="number"
        :caption="$t('tools.erp_integration.sync_log_duration')"
        :width="110"
        cell-template="durationCell"
      />
      <DxColumn
        data-field="error_summary"
        :caption="$t('tools.erp_integration.sync_log_error')"
        :min-width="220"
      />
      <DxColumn
        data-field="actions"
        :caption="$t('tools.erp_integration.sync_log_actions')"
        :width="110"
        cell-template="actionsCell"
      />

      <template #statusCell="{ data }">
        <span class="log-status" :class="statusClass(data.value)">
          {{ statusLabel(data.value) }}
        </span>
      </template>

      <template #actionsCell="{ data }">
        <button
          v-if="data.data.status === 'running'"
          type="button"
          class="btn btn-sm btn-outline-danger"
          :disabled="cancellingUuid === data.data.id"
          @click="requestCancel(data.data)"
        >
          {{ cancellingUuid === data.data.id ? $t('common.please_wait') : $t('tools.erp_integration.sync_log_cancel') }}
        </button>
      </template>

      <template #durationCell="{ data }">
        <span>{{ formatDuration(data.value) }}</span>
      </template>

      <template #logDetail="{ data }">
        <div class="log-detail">
          <div class="log-detail__row">
            <strong>{{ $t("tools.erp_integration.sync_summary_fetched", { count: data.data.fetched_count }) }}</strong>
          </div>
          <div class="log-detail__row">
            {{ $t("tools.erp_integration.sync_summary_processed", { count: data.data.processed_count }) }}
          </div>
          <div class="log-detail__row">
            {{ $t("tools.erp_integration.sync_summary_skipped", { count: data.data.skipped_count }) }}
          </div>
          <div class="log-detail__row">
            {{ $t("tools.erp_integration.sync_summary_inserted", { count: data.data.inserted_count }) }}
          </div>
          <div class="log-detail__row">
            {{ $t("tools.erp_integration.sync_summary_updated", { count: data.data.updated_count }) }}
          </div>
          <div class="log-detail__row">
            {{ $t("tools.erp_integration.sync_summary_untouched", { count: data.data.untouched_count }) }}
          </div>
          <div class="log-detail__row log-detail__row--error" v-if="data.data.error_message">
            <div class="log-detail__label">
              {{ $t("tools.erp_integration.sync_log_server_error") }}
            </div>
            <div class="log-detail__meta" v-if="data.data.request_url">
              {{ data.data.request_method }} {{ data.data.request_url }}
              <span v-if="data.data.error_status">— HTTP {{ data.data.error_status }}</span>
            </div>
            <div class="log-detail__meta" v-if="data.data.error_exception">
              {{ data.data.error_exception }}
              <span v-if="data.data.error_file">
                @ {{ data.data.error_file }}<template v-if="data.data.error_line">:{{ data.data.error_line }}</template>
              </span>
            </div>
            <pre class="log-detail__trace">{{ data.data.error_message }}</pre>
            <div class="log-detail__label" v-if="data.data.error_previous">
              {{ $t("tools.erp_integration.sync_log_error_previous") }}
            </div>
            <pre class="log-detail__trace" v-if="data.data.error_previous">{{ data.data.error_previous }}</pre>
            <div class="log-detail__label" v-if="data.data.error_trace">
              Trace
            </div>
            <pre class="log-detail__trace" v-if="data.data.error_trace">{{ data.data.error_trace }}</pre>
            <div class="log-detail__label" v-if="data.data.response_body">
              {{ $t("tools.erp_integration.sync_log_response_body") }}
            </div>
            <pre class="log-detail__trace" v-if="data.data.response_body">{{ data.data.response_body }}</pre>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="copyError(data.data)"
            >
              {{ $t("tools.erp_integration.sync_log_copy_error") }}
            </button>
          </div>

          <div class="log-detail__row" v-if="data.data.raw_entry">
            <button
              type="button"
              class="btn btn-sm btn-link log-diag-toggle"
              @click="toggleRaw(data.data.id)"
            >
              {{ $t("tools.erp_integration.sync_log_raw_record") }}
            </button>
            <pre class="log-detail__trace" v-if="expandedRawIds.includes(data.data.id)">{{ formatRaw(data.data.raw_entry) }}</pre>
          </div>
        </div>
      </template>
    </DxDataGrid>
  </div>
</template>

<script>
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxHeaderFilter,
  DxFilterRow,
  DxMasterDetail,
} from "devextreme-vue/data-grid";
import {
  fetchSyncLogs,
  cancelSyncLog,
  isRemoteLoggingAvailable,
  isRemoteWriteAvailable,
  getLastRawResponse,
  resetRemoteLoggingAvailability,
  clearLocalSyncLogs,
} from "@/pages/tools/erp-integration/services/erpSyncLogService";
import { translateErpEntityLabel } from "@/pages/tools/erp-integration/services/erpEntityLabels";
import notify from "devextreme/ui/notify";
import { custom } from "devextreme/ui/dialog";

export default {
  name: "ErpSyncLogPanel",
  components: {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxHeaderFilter,
    DxFilterRow,
    DxMasterDetail,
  },
  props: {
    entityCode: {
      type: String,
      default: "",
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rawLogs: [],
      gridPageSize: 50,
      loading: false,
      remoteAvailable: true,
      writeAvailable: true,
      showDiagnostics: false,
      rawResponse: null,
      expandedRawIds: [],
      cancellingUuid: "",
    };
  },
  computed: {
    normalizedEntityCode() {
      return (this.entityCode || "").toString().trim().toUpperCase();
    },
    logs() {
      // A megnevezés mindig az aktuális nyelven jelenik meg, nem a mentéskorin.
      return this.rawLogs.map((item) => ({
        ...item,
        entity_label: translateErpEntityLabel(this.$t.bind(this), item.entity_code, item.entity_name),
        error_summary: this.buildErrorSummary(item),
        source_label: item.is_local
          ? this.$t("tools.erp_integration.sync_log_source_local")
          : this.$t("tools.erp_integration.sync_log_source_server"),
      }));
    },
    /**
     * A szerver jelzi, ha a napló tábla hiányzik az adott cég adatbázisából
     * (le nem futott migráció). Enélkül ez üres naplónak látszana.
     */
    storageProblem() {
      const raw = this.rawResponse;

      if (!raw) {
        return "";
      }

      // A lekérés el sem jutott a szerverig, vagy hibát adott vissza.
      if (raw.error) {
        return `${raw.error}${raw.body ? ` — ${this.shortJson(raw.body)}` : ""}`;
      }

      const storage = raw.storage;

      if (!storage || storage.available !== false) {
        return "";
      }

      const parts = [];

      if (storage.error) {
        parts.push(storage.error);
      }

      if (storage.database) {
        parts.push(`(${storage.table || "erp_sync_logs"} @ ${storage.database})`);
      }

      return parts.join(" ");
    },
    localCount() {
      return this.rawLogs.filter((item) => item.is_local).length;
    },
    diagnosticsText() {
      const info = {
        read_endpoint_available: this.remoteAvailable,
        client_write_allowed: this.writeAvailable,
        rows_loaded: this.rawLogs.length,
        local_rows: this.rawLogs.filter((item) => item.is_local).length,
        visible: this.visible,
        entity_filter: this.normalizedEntityCode || "(none)",
        active_tab_entity: this.entityCode || "(none)",
        last_requested_entity_code: this.rawResponse?.requestedEntityCode || null,
        last_server_response: this.rawResponse,
      };

      try {
        return JSON.stringify(info, null, 2);
      } catch (e) {
        return String(info);
      }
    },
  },
  methods: {
    onOptionChanged(e) {
      if (e.fullName === "paging.pageSize" && e.value !== e.previousValue) {
        this.gridPageSize = e.value;
        this.reload();
      }
    },

    statusLabel(value) {
      if (value === "error") {
        return this.$t("tools.erp_integration.sync_log_status_error");
      }

      if (value === "oprit") {
        return this.$t("tools.erp_integration.sync_log_status_cancelled");
      }

      if (value === "running") {
        return this.$t("tools.erp_integration.sync_log_status_running");
      }

      return this.$t("tools.erp_integration.sync_log_status_success");
    },

    statusClass(value) {
      if (value === "error") {
        return "log-status--error";
      }

      if (value === "oprit") {
        return "log-status--cancelled";
      }

      return value === "running" ? "log-status--running" : "log-status--ok";
    },

    toggleRaw(id) {
      const index = this.expandedRawIds.indexOf(id);

      if (index >= 0) {
        this.expandedRawIds.splice(index, 1);
        return;
      }

      this.expandedRawIds.push(id);
    },
    formatRaw(raw) {
      try {
        return JSON.stringify(raw, null, 2);
      } catch (e) {
        return String(raw);
      }
    },
    buildErrorSummary(item) {
      if (!item?.error_message) {
        return "";
      }

      // A gridben csak az első érdemi sor látszik, a részletek a detail nézetben.
      const firstMeaningfulLine = String(item.error_message)
        .split("\n")
        .map((line) => line.trim())
        .find((line) => line && !/^HTTP \d+/i.test(line) && !/^(GET|POST|PUT|DELETE|PATCH)\s/i.test(line));

      const prefix = item.error_status ? `HTTP ${item.error_status}: ` : "";

      return `${prefix}${firstMeaningfulLine || item.error_message}`;
    },
    copyError(item) {
      const parts = [
        item?.error_status ? `HTTP ${item.error_status}` : "",
        item?.request_url ? `${item.request_method} ${item.request_url}` : "",
        item?.error_exception ? `${item.error_exception} @ ${item.error_file}:${item.error_line}` : "",
        item?.error_message || "",
        item?.error_previous ? `\nCaused by:\n${item.error_previous}` : "",
        item?.error_trace ? `\nTrace:\n${item.error_trace}` : "",
        item?.response_body ? `\nResponse:\n${item.response_body}` : "",
      ].filter(Boolean);

      const text = parts.join("\n");

      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(() => {});
      }
    },
    clearLocal() {
      clearLocalSyncLogs();
      this.reload();
    },
    requestCancel(item) {
      custom({
        title: this.$t("tools.erp_integration.sync_log_cancel_title"),
        messageHtml: this.$t("tools.erp_integration.sync_log_cancel_body"),
      })
        .show()
        .done(() => this.performCancel(item));
    },
    async performCancel(item) {
      this.cancellingUuid = item.id;

      try {
        await cancelSyncLog(item.id);
        notify(this.$t("tools.erp_integration.sync_log_cancel_success"), "success", 2500);
        await this.reload();
      } catch (e) {
        notify(
          e?.response?.data?.message || e?.message || this.$t("tools.erp_integration.sync_log_cancel_title"),
          "error",
          3000
        );
      } finally {
        this.cancellingUuid = "";
      }
    },
    shortJson(value) {
      try {
        return JSON.stringify(value).slice(0, 300);
      } catch (e) {
        return String(value).slice(0, 300);
      }
    },
    formatDuration(value) {
      const ms = Number(value || 0);

      if (ms < 1000) {
        return `${ms} ms`;
      }

      return `${(ms / 1000).toFixed(1)} s`;
    },
    clearLoadedLogs() {
      this.rawLogs = [];
      this.rawResponse = null;
      this.expandedRawIds = [];
    },
    async reload() {
      if (!this.visible || !this.normalizedEntityCode) {
        this.clearLoadedLogs();
        return;
      }

      this.loading = true;

      // Minden frissítésnél újrapróbáljuk a szervert: egy korábbi hálózati
      // hiba nem tilthatja le véglegesen a szerver oldali napló olvasását.
      resetRemoteLoggingAvailability();

      try {
        this.rawLogs = await fetchSyncLogs({ entityCode: this.normalizedEntityCode, limit: this.gridPageSize });
      } finally {
        this.remoteAvailable = isRemoteLoggingAvailable();
        this.writeAvailable = isRemoteWriteAvailable();
        this.rawResponse = getLastRawResponse();
        this.loading = false;
      }
    },
  },
  mounted() {
    if (this.visible) {
      this.reload();
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.reload();
        return;
      }

      this.clearLoadedLogs();
    },
    entityCode() {
      if (this.visible) {
        this.reload();
        return;
      }

      this.clearLoadedLogs();
    },
  },
};
</script>

<style scoped>
.erp-sync-log-grid {
  height: auto;
}

.erp-sync-log-grid ::v-deep .dx-datagrid-rowsview .dx-data-row {
  height: 36px;
  min-height: 36px;
}

.erp-sync-log-grid ::v-deep .dx-datagrid-rowsview .dx-row > td {
  padding-top: 4px;
  padding-bottom: 4px;
}

.log-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.log-hint {
  font-size: 12px;
}

.log-diag-toggle {
  padding: 0;
  font-size: 12px;
}

.log-diagnostics {
  margin-bottom: 12px;
}

.log-storage-warning {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 12px;
  padding: 8px 12px;
  border: 1px solid #f0c36d;
  border-left: 3px solid #e0a800;
  border-radius: 4px;
  background-color: #fdf7e3;
  color: #7a5b00;
  font-size: 12px;
}

.log-status {
  font-weight: 600;
}

.log-status--ok {
  color: #2e7d32;
}

.log-status--running {
  color: #b26a00;
}

.log-status--error {
  color: #c62828;
}

.log-status--cancelled {
  color: #6b7280;
}

.log-detail {
  padding: 8px 12px;
  font-size: 13px;
}

.log-detail__row {
  padding: 2px 0;
}

.log-detail__row--error {
  color: #c62828;
  white-space: pre-wrap;
}

.log-detail__label {
  font-weight: 600;
  margin-top: 6px;
}

.log-detail__meta {
  color: #6b6b6b;
  font-size: 12px;
  word-break: break-all;
}

.log-detail__trace {
  max-height: 320px;
  overflow: auto;
  background: #fbeaea;
  border: 1px solid #f0c4c4;
  border-radius: 4px;
  padding: 8px;
  margin: 6px 0;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
